import React, {useState} from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Divider from '@mui/material/Divider';
import PropTypes from 'prop-types';
import TwoToneCheckbox from '../../TwoToneCheckbox.jsx';

/**
 * @param {CheckboxWithOptionsLineItem.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function CheckboxWithOptionsLineItem(props) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentPageCheckbox, setCurrentPageCheckbox] = useState(false);
    const [allPagesCheckbox, setAllPagesCheckbox] = useState(false);

    const isAllPagesChecked = props.materials.length > 0 &&
        props.materials.every((material) => props.selectedMaterialIds.includes(material.id));

    const isAnyMaterialSelected = props.selectedMaterialIds.length > 0;

    const isAllPagesIndeterminate = isAnyMaterialSelected && !isAllPagesChecked;

    const toggleMenu = (event) => {
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleNestedCheckboxChange = (option) => {
        if (option === 'current') {
            if (allPagesCheckbox) {
                setAllPagesCheckbox(false);
            }
            if (!currentPageCheckbox) {
                selectCurrentPageMaterials();
                setCurrentPageCheckbox(true);
            } else {
                deselectCurrentPageMaterials();
                setCurrentPageCheckbox(false);
            }
        } else if (option === 'all') {
            if (currentPageCheckbox) {
                setCurrentPageCheckbox(false);
            }
            if (!isAllPagesChecked) {
                selectAllMaterials();
                setAllPagesCheckbox(true);
            } else if (isAllPagesChecked && !allPagesCheckbox) {
                setAllPagesCheckbox(true);
            } else {
                deselectAllMaterials();
                setAllPagesCheckbox(false);
            }
        }
    };

    const selectCurrentPageMaterials = () => {
        const currentPageMaterialIds = props.filteredMaterials.map((material) => material.id);
        const newSelectedMaterialIds = Array.from(new Set([...currentPageMaterialIds]));
        props.setSelectedMaterialIds(newSelectedMaterialIds);
    };

    const deselectCurrentPageMaterials = () => {
        const currentPageMaterialIds = new Set(props.filteredMaterials.map((material) => material.id));
        const newSelectedMaterialIds = props.selectedMaterialIds.filter((id) => !currentPageMaterialIds.has(id));
        props.setSelectedMaterialIds(newSelectedMaterialIds);
    };

    const selectAllMaterials = () => {
        const allMaterialIds = props.materials.map((material) => material.id);
        props.setSelectedMaterialIds(allMaterialIds);
    };

    const deselectAllMaterials = () => {
        props.setSelectedMaterialIds([]);
    };

    return (
        <div>
            <IconButton
                onClick={toggleMenu}
                disabled={props.disabled}
                sx={{
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiTouchRipple-root': {
                        display: 'none',
                    },
                }}>
                <TwoToneCheckbox
                    checked={isAllPagesChecked}
                    disabled={props.disabled}
                    indeterminate={isAllPagesIndeterminate}
                />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={toggleMenu}
            >
                <MenuItem>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <TwoToneCheckbox
                                    checked={currentPageCheckbox}
                                    onChange={() => handleNestedCheckboxChange('current')}
                                    disabled={props.disabled}
                                />
                            }
                            label="Select items in displayed page"
                        />
                    </FormGroup>
                </MenuItem>
                <Divider />
                <MenuItem>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <TwoToneCheckbox
                                    checked={allPagesCheckbox}
                                    onChange={() => handleNestedCheckboxChange('all')}
                                    disabled={props.disabled}
                                />
                            }
                            label="Select items in all pages"
                        />
                    </FormGroup>
                </MenuItem>
            </Menu>
        </div>
    );
}

CheckboxWithOptionsLineItem.propTypes = {
    setSelectedMaterialIds: PropTypes.func.isRequired,
    selectedMaterialIds: PropTypes.array.isRequired,
    materials: PropTypes.array.isRequired,
    filteredMaterials: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
};
